import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Sparkles, Send, Loader2, Trash2, X } from 'lucide-react';

/**
 * Spark — the CodeITT AI learning companion.
 * Reusable AI chat for students and teachers. The backend auto-detects the user's role
 * and applies the right system prompt.
 */
export const AIBuddyPanel = ({ open, onClose, mode = 'student' }) => {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const scrollRef = useRef(null);

  const config = mode === 'teacher'
    ? {
        title: 'Spark',
        subtitle: 'Your CodeITT Teaching Companion',
        placeholder: 'Ask for a lesson idea, quiz, parent feedback draft…',
        intro: 'Hi! Need help with lesson plans, quizzes, projects, or parent feedback? Ask away.',
      }
    : {
        title: 'Spark',
        subtitle: 'Your CodeITT Learning Companion',
        placeholder: "What's on your mind today?",
        intro: "Hey! I'm Spark. Stuck on a problem? Ask me for hints — I'll guide you, not give it away.",
      };

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoadingHistory(true);
      try {
        const res = await api.get('/ai-buddy/history?limit=100');
        if (!cancelled) setMessages(res.data?.messages || []);
      } catch {
        if (!cancelled) setMessages([]);
      } finally {
        if (!cancelled) setLoadingHistory(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const handleSend = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setDraft('');
    const optimistic = { role: 'user', content: text, created_at: new Date().toISOString() };
    setMessages((prev) => [...prev, optimistic]);
    setSending(true);
    try {
      const res = await api.post('/ai-buddy/chat', { message: text });
      const reply = res.data?.reply || '';
      setMessages((prev) => [...prev, { role: 'assistant', content: reply, created_at: new Date().toISOString() }]);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Spark is unavailable right now.';
      toast.error(typeof detail === 'string' ? detail : 'Spark error');
      setMessages((prev) => prev.slice(0, -1)); // rollback optimistic
      setDraft(text);
    } finally {
      setSending(false);
    }
  };

  const handleClear = async () => {
    if (!window.confirm('Clear this conversation?')) return;
    try {
      await api.delete('/ai-buddy/history');
      setMessages([]);
      toast.success('Conversation cleared');
    } catch {
      toast.error('Failed to clear history');
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="max-w-2xl p-0 overflow-hidden border-0 bg-white"
        data-testid="ai-buddy-dialog"
      >
        {/* Header — CodeITT navy → violet gradient */}
        <div
          className="px-5 py-4 flex items-center justify-between"
          style={{ background: 'linear-gradient(135deg, #0F172A 0%, #4C1D95 60%, #7C3AED 100%)', color: 'white' }}
        >
          <DialogHeader className="text-left p-0 m-0">
            <DialogTitle className="flex items-center gap-2 text-white text-base font-bold" style={{ fontFamily: "'Manrope', sans-serif" }}>
              <Sparkles className="w-5 h-5 text-violet-200" />
              {config.title}
            </DialogTitle>
            <p className="text-[13px] text-violet-100 mt-0.5 font-medium">{config.subtitle}</p>
          </DialogHeader>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClear}
              className="text-white hover:bg-white/15 h-8 px-2"
              title="Clear conversation"
              data-testid="ai-buddy-clear"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-white hover:bg-white/15 h-8 px-2"
              data-testid="ai-buddy-close"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Message area — very light background, dark navy text */}
        <div
          ref={scrollRef}
          className="spark-chat-window px-4 py-4 overflow-y-auto"
          style={{ height: '420px', fontFamily: "'Inter', sans-serif" }}
          data-testid="ai-buddy-messages"
        >
          {loadingHistory ? (
            <div className="flex items-center justify-center h-full text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-6">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center mb-3 shadow-lg shadow-violet-500/30">
                <Sparkles className="w-7 h-7 text-white" />
              </div>
              <p className="text-[15px] font-semibold text-slate-900" style={{ fontFamily: "'Manrope', sans-serif" }}>{config.intro}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  data-testid={`ai-buddy-msg-${m.role}-${i}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-[14px] leading-relaxed whitespace-pre-wrap break-words shadow-sm ${
                      m.role === 'user'
                        ? 'spark-msg-user rounded-br-md'
                        : 'spark-msg-assistant rounded-bl-md'
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start" data-testid="ai-buddy-typing">
                  <div className="spark-msg-assistant rounded-2xl rounded-bl-md px-4 py-2 text-sm text-slate-500 flex items-center gap-2 shadow-sm">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Spark is thinking…
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Input row — light background, dark text, visible placeholder */}
        <div className="bg-white border-t border-slate-200 px-3 py-3 flex items-end gap-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKey}
            placeholder={config.placeholder}
            rows={1}
            className="spark-input flex-1 resize-none rounded-xl px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-400"
            style={{ minHeight: '40px', maxHeight: '120px', fontFamily: "'Inter', sans-serif" }}
            data-testid="ai-buddy-input"
            disabled={sending}
          />
          <Button
            onClick={handleSend}
            disabled={sending || !draft.trim()}
            className="spark-send-btn rounded-xl px-4 h-10"
            data-testid="ai-buddy-send"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AIBuddyPanel;
